import { useEffect, RefObject, useState } from "react";
import { useAtomValue } from "jotai";
import {
  SEGMENTATION_CURSOR_SVG,
  getBumpCursorSVG,
  getRegionGrowCursorSVG,
  getPositiveRefineCursorSVG,
  getNegativeRefineCursorSVG,
} from "../utils/segmentationConstants";
import { nnSessionActiveAtom } from "../atoms/segmentationAtoms";

/**
 * Sets the cursor on a viewport element (and its canvas child) based on the
 * active segmentation tool. Cornerstone overrides CSS cursor, so we set it
 * directly on the elements.
 *
 * When an nnInteractive session is active:
 * - Shift held → green cross (positive refinement)
 * - Ctrl/Meta held → red cross (negative refinement)
 */
export function useSegmentationCursor(
  viewportRef: RefObject<HTMLDivElement | null>,
  activeTool: string,
  bumpRadius?: number,
) {
  const [modifierKey, setModifierKey] = useState<"shift" | "ctrl" | null>(null);
  const nnActive = useAtomValue(nnSessionActiveAtom);

  // Track Shift/Ctrl key state globally
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Shift") setModifierKey("shift");
      else if (e.key === "Control" || e.key === "Meta") setModifierKey("ctrl");
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key === "Shift" && modifierKey === "shift") setModifierKey(null);
      else if ((e.key === "Control" || e.key === "Meta") && modifierKey === "ctrl")
        setModifierKey(null);
    };
    const onBlur = () => setModifierKey(null);

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("blur", onBlur);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("blur", onBlur);
    };
  }, [modifierKey]);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const canvas = el.querySelector("canvas");
    let cursor = "";

    // When nnInteractive session is active, show refinement cursors on modifier keys
    if (nnActive && modifierKey === "shift") {
      cursor = getPositiveRefineCursorSVG();
    } else if (nnActive && modifierKey === "ctrl") {
      cursor = getNegativeRefineCursorSVG();
    } else if (activeTool === "lasso") {
      cursor = SEGMENTATION_CURSOR_SVG;
    } else if (activeTool === "bump") {
      cursor = getBumpCursorSVG(bumpRadius ?? 28);
    } else if (activeTool === "regionGrow") {
      cursor = getRegionGrowCursorSVG();
    }

    el.style.cursor = cursor;
    if (canvas) canvas.style.cursor = cursor;
  }, [activeTool, bumpRadius, modifierKey, nnActive]);
}
